import { Typography } from '@workspace/ui/components/Typography';
import { cn } from '@workspace/ui/lib/utils';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { company } from '~/company/Company';
import { Button, SIZE, VARIANT } from '~/components/isolated/Button';
import { useGlobal } from '~/hooks/useGlobal';
import type { ReviewMedia } from '~/types/Product/interface';

import ReviewCard from './components/ReviewCard';
import ReviewMediaLightbox from './components/ReviewMediaLightbox';
import type { MediaWithReview, ProductReviewsProps } from './interfaces';

import styles from './ProductReviews.module.scss';

const ProductReviews = ({
  className,
  reviews = [],
  totalCount = 0,
  onLoadMore,
  canLoadMore,
  ozonUrl,
}: ProductReviewsProps) => {
  const { t } = useTranslation();
  const { isMobile } = useGlobal();

  const [lightboxState, setLightboxState] = useState<{
    isOpen: boolean;
    initialIndex: number;
  }>({
    isOpen: false,
    initialIndex: 0,
  });

  const allMediaWithReviews = useMemo<MediaWithReview[]>(() => {
    const result: MediaWithReview[] = [];
    for (const review of reviews) {
      if (review.media) {
        for (const media of review.media) {
          result.push({ media, review });
        }
      }
    }
    return result;
  }, [reviews]);

  const handleViewAll = () => {
    window.open(ozonUrl, '_blank');
  };

  const handleMediaClick = useCallback(
    (mediaIndex: number, reviewMedia: ReviewMedia[]) => {
      let globalIndex = 0;
      let targetGlobalIndex = 0;

      for (const review of reviews) {
        if (review.media) {
          for (let i = 0; i < review.media.length; i++) {
            if (review.media === reviewMedia && i === mediaIndex) {
              targetGlobalIndex = globalIndex;
            }
            globalIndex++;
          }
        }
      }

      setLightboxState({
        isOpen: true,
        initialIndex: targetGlobalIndex,
      });
    },
    [reviews],
  );

  const handleCloseLightbox = useCallback(() => {
    setLightboxState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  if (!reviews.length) return null;

  const ProductReviewsComponent = isMobile
    ? Typography.Header100Semibold
    : company.match({
        mcosmetic: Typography.Header400Semibold,
        default: Typography.Header400Medium,
      });
  const TotalCountComponent = isMobile
    ? Typography.Header100Medium
    : Typography.Header400Medium;

  return (
    <section className={cn(styles.root, className)}>
      <div className={styles.header}>
        <ProductReviewsComponent as="h2" className={styles.title}>
          {t('productReviews')}
        </ProductReviewsComponent>
        <TotalCountComponent className={styles.count}>
          {totalCount}
        </TotalCountComponent>
      </div>

      <div className={styles.reviewsList}>
        {reviews.map((review, index) => (
          <ReviewCard
            key={review.id}
            review={review}
            showDivider={index < reviews.length - 1}
            onMediaClick={handleMediaClick}
          />
        ))}
      </div>

      {company.isMarketplaceVariant ? (
        <Button
          variant={VARIANT.secondary}
          size={isMobile ? SIZE.L : SIZE.XL}
          className={cn(
            styles.viewAllButton,
            company.match({
              mcosmetic: 'rounded-full! border-none! bg-surface-transparent-2!',
            }),
          )}
          onClick={handleViewAll}
          endIcon="link-external-01"
        >
          <Typography.Text400Semibold as="span">
            {t('viewAll')}
          </Typography.Text400Semibold>
        </Button>
      ) : canLoadMore ? (
        <Button
          variant={VARIANT.secondary}
          size={isMobile ? SIZE.L : SIZE.XL}
          className={styles.viewAllButton}
          onClick={onLoadMore}
        >
          {t('showMore')}
        </Button>
      ) : null}

      <ReviewMediaLightbox
        isOpen={lightboxState.isOpen}
        onClose={handleCloseLightbox}
        allMediaWithReviews={allMediaWithReviews}
        initialIndex={lightboxState.initialIndex}
      />
    </section>
  );
};

export default ProductReviews;
